import {
  parseGroupConsole,
  parseGroupClaim,
  parseGroupClaimRevoked,
  parseGroupInvite,
} from '../../shared/wire';
import type { GeofenceShape } from '../../shared/wire';

export async function apiGetGroupConsole(groupPath: string, since?: number | null){
  // Participant-only group console (ADR 0024/0025/0026): markers + geofence
  // shapes + breach events + per-device commands/full-precision pings. The
  // 200/404 IS the participant gate; 404 throws with .status (callers decide —
  // the gc-modal toasts, the team poll stops quietly). `since` windows the breach
  // events (markers/geofences are always the full current set). Cookie-authed.
  let url = `/api/v1/groups/${groupPath}/console`;
  if(since != null) url += `?since=${since}`;
  const res = await fetch(url, { credentials: 'include' });
  if(!res.ok){ const e: any = new Error('group-console ' + res.status); e.status = res.status; throw e; }
  return parseGroupConsole(await res.json());
}

export async function apiGroupClaim(groupPath: string){
  const res = await fetch(`/api/v1/groups/${groupPath}/claim`, {
    method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: '{}',
  });
  if(!res.ok){ const e: any = new Error('group-claim ' + res.status); e.status = res.status; throw e; }
  return parseGroupClaim(await res.json());
}

export async function apiGroupClaimRevoke(groupPath: string){
  const res = await fetch(`/api/v1/groups/${groupPath}/claim/revoke`, {
    method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: '{}',
  });
  if(!res.ok){ const e: any = new Error('group-claim-revoke ' + res.status); e.status = res.status; throw e; }
  return parseGroupClaimRevoked(await res.json());
}

export async function apiMintInvite(groupPath: string, body: { expires?: string; max_uses?: number | null } = {}){
  const res = await fetch(`/api/v1/groups/${groupPath}/invites`, {
    method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
  });
  if(!res.ok){ const e: any = new Error('mint-invite ' + res.status); e.status = res.status; throw e; }
  return parseGroupInvite(await res.json());
}

// Marker CRUD (ADR 0026). POST → {id, created_at}; PATCH → {id, updated_at};
// DELETE → {deleted_at}. All same-origin cookie-authed; non-OK throws with .status
// (callers map 403=cap, 404=not-author/claim-lapsed, 429=rate-limited).
export async function apiCreateMarker(groupPath: string, lat: number, lng: number, label: string | null, kind: string){
  const res = await fetch(`/api/v1/groups/${groupPath}/markers`, {
    method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ lat, lng, label, kind }),
  });
  if(!res.ok){ const e: any = new Error('marker-create ' + res.status); e.status = res.status; throw e; }
  const j: any = await res.json();
  return { id: Number(j.id), created_at: Number(j.created_at) };
}

export async function apiUpdateMarker(groupPath: string, id: number, lat: number, lng: number, label: string | null, kind: string){
  const res = await fetch(`/api/v1/groups/${groupPath}/markers/${id}`, {
    method: 'PATCH', credentials: 'include', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ lat, lng, label, kind }),
  });
  if(!res.ok){ const e: any = new Error('marker-update ' + res.status); e.status = res.status; throw e; }
  const j: any = await res.json();
  return { updated_at: Number(j.updated_at) };
}

export async function apiDeleteMarker(groupPath: string, id: number){
  const res = await fetch(`/api/v1/groups/${groupPath}/markers/${id}`, { method: 'DELETE', credentials: 'include' });
  if(!res.ok){ const e: any = new Error('marker-delete ' + res.status); e.status = res.status; throw e; }
  return res.json();
}

// Geofence CRUD (ADR 0026, OWNER-only). POST → {id}; PATCH → {id, updated_at};
// DELETE → {deleted_at}. shape must match parseGeofenceShape (circle XOR polygon).
// Non-OK throws with .status (404 = non-owner / unclaimed, 403 = cap).
export async function apiCreateGeofence(groupPath: string, shape: GeofenceShape, label: string | null, kind: string){
  const res = await fetch(`/api/v1/groups/${groupPath}/geofences`, {
    method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ shape, label, kind }),
  });
  if(!res.ok){ const e: any = new Error('geofence-create ' + res.status); e.status = res.status; throw e; }
  const j: any = await res.json();
  return { id: Number(j.id) };
}

export async function apiUpdateGeofence(groupPath: string, id: number, shape: GeofenceShape, label: string | null, kind: string){
  const res = await fetch(`/api/v1/groups/${groupPath}/geofences/${id}`, {
    method: 'PATCH', credentials: 'include', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ shape, label, kind }),
  });
  if(!res.ok){ const e: any = new Error('geofence-update ' + res.status); e.status = res.status; throw e; }
  const j: any = await res.json();
  return { updated_at: Number(j.updated_at) };
}

export async function apiDeleteGeofence(groupPath: string, id: number){
  const res = await fetch(`/api/v1/groups/${groupPath}/geofences/${id}`, { method: 'DELETE', credentials: 'include' });
  if(!res.ok){ const e: any = new Error('geofence-delete ' + res.status); e.status = res.status; throw e; }
  return res.json();
}
